<template>
    <div class="main-content">
        <div class="toast-container position-fixed top-0 end-0 p-3" style="z-index: 1060">
            <transition-group name="slide-fade">
                <div v-for="alert in alerts" :key="alert.id"
                    class="toast show align-items-center border-0 shadow-lg mb-2"
                    :class="'bg-' + alert.type + '-subtle text-' + alert.type" role="alert">
                    <div class="d-flex p-3">
                        <i :class="alert.icon" class="me-3 fs-5"></i>
                        <div class="toast-body p-0 fw-medium">
                            {{ alert.message }}
                        </div>
                        <button type="button" class="btn-close ms-auto" @click="removeAlert(alert.id)"></button>
                    </div>
                </div>
            </transition-group>
        </div>

        <header class="content-header mb-2 mt-n5">
            <nav aria-label="breadcrumb">
                <ol class="breadcrumb mb-0">
                    <li class="breadcrumb-item"> <router-link class="nav-link active" to="/">Início</router-link></li>
                    <li class="breadcrumb-item active text-dark small">Serviços</li>
                </ol>
            </nav>
        </header>

        <div class="card p-4 shadow-subtle rounded-4 border-0">
            <div class="d-flex justify-content-between align-items-center mb-4 pb-2 border-bottom">
                <div>
                    <h4 class="mb-0 fw-bold">Serviços</h4>
                    <span class="text-muted fs-7">Exibindo {{ services.length }} de {{ totalItems }}</span>
                </div>

                <div class="d-flex gap-2 align-items-center">
                    <transition name="fade">
                        <div v-if="selectedIds.length > 0" class="d-flex align-items-center me-2 pe-3 border-end">
                            <button class="btn btn-pmg-danger btn-sm px-3"
                                @click="confirmDelete(selectedIds.length, true)">
                                <i class="bi bi-trash3 me-1"></i> Excluir ({{
                                    selectedIds.length ? selectedIds.length : 'serviço' }})
                            </button>
                        </div>
                    </transition>
                    <div class="dropdown me-2 pe-3 border-end">
                        <button class="btn btn-pmg-light dropdown-toggle" type="button" data-bs-toggle="dropdown"
                            aria-expanded="false">
                            <i class="bi bi-printer me-1"></i> Relatórios / Ferramentas
                        </button>
                        <ul class="dropdown-menu shadow border-0 py-2">
                            <li>
                                <h6 class="dropdown-header text-uppercase small fw-bold opacity-75">Exportar Dados</h6>
                            </li>
                            <li>
                                <a class="dropdown-item py-2" href="#" @click.prevent="openReportModal('pdf')">
                                    <i class="bi bi-file-earmark-pdf text-danger me-2"></i> Relatório Geral (PDF)
                                </a>
                            </li>
                            <li>
                                <a class="dropdown-item py-2" href="#" @click.prevent="openReportModal('xlsx')">
                                    <i class="bi bi-file-earmark-excel text-success me-2"></i> Planilha Completa (Excel)
                                </a>
                            </li>
                        </ul>
                    </div>
                     <button class="btn btn-pmg-light" @click="refreshData" :disabled="loading" title="Atualizar tela">
                        <i class="bi bi-arrow-clockwise" :class="{ 'spin-animation': loading }"></i>
                     </button>
                    <button class="btn btn-pmg-light" @click="showFilter = !showFilter"
                        :class="{ 'active': showFilter }">
                        <i class="bi bi-funnel"></i> Filtrar
                    </button>

                    <button class="btn btn-pmg-primary" data-bs-toggle="modal" data-bs-target="#modalService">
                        <i class="bi bi-plus-lg me-1"></i> Novo Serviço
                    </button>
                </div>
            </div>

            <div class="collapse" :class="{ 'show': showFilter }">
                <div class="card card-body mb-4 border-0 bg-light rounded-3">
                    <div class="row g-3">
                        <div class="col-md-10">
                            <input v-model="filter.description" type="text" class="form-control form-control-pmg"
                                placeholder="Filtrar por descrição...">
                        </div>
                        <div class="col-md-2 d-flex">
                            <button class="btn btn-dark w-100 btn-pmg-dark fw-bold" @click="applyFilter">
                                <i class="bi bi-search me-1"></i> Filtrar
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            <div class="table-responsive">
                <table class="table table-pmg align-middle">
                    <thead>
                        <tr>
                            <th style="width: 5%">
                                <input type="checkbox" class="form-check-input" @change="toggleAll"
                                    :checked="isAllSelected">
                            </th>
                            <th style="width: 25%">Descrição</th>
                            <th style="width: 15%">Custo</th>
                            <th style="width: 15%">Venda</th>
                            <th style="width: 25%">Estoque</th>
                            <th style="width: 10%" class="text-end">Ações</th>
                        </tr>
                    </thead>

                    <tbody v-if="services.length > 0">
                        <tr v-for="item in services" :key="item.id"
                            :class="{ 'selected-row': selectedIds.includes(item.id) }">
                            <td><input type="checkbox" class="form-check-input" :value="item.id" v-model="selectedIds">
                            </td>
                            <td class="fw-bold text-dark">
                                <div class="pmg-desc-cell text-truncate-custom">{{ item.description }}</div>
                            </td>
                            <td class="text-muted">{{ item.cost_price ? formatCurrency(item.cost_price) : '-' }}</td>
                            <td class="fw-medium text-success">{{ formatCurrency(item.sale_price) }}</td>
                            <td>
                                <!-- 1. Alerta do Estoque Geral (Calculado de forma dinâmica ou mantido como indicador geral) -->
                                <div class="mb-1 d-flex align-items-center">
                                    <!-- Opcional: Se quiser alertar caso o estoque total seja menor que a soma dos estoques mínimos das empresas -->
                                    <span
                                        :class="isGlobalStockCritical(item) ? 'badge bg-danger-subtle text-danger' : 'text-dark fw-bold'"
                                        class="px-2 py-1 rounded fs-6">
                                        {{ item.stock || 0 }} {{ item.unit_of_measure }}

                                        <span class="text-muted ms-1"
                                            style="font-size: 0.75rem; text-transform: uppercase;">Total</span>
                                    </span>
                                </div>

                                <!-- 2. Estoque e Alertas individuais de cada Empresa -->
                                <div class="d-flex flex-wrap gap-1 mt-1">
                                    <template v-if="item.branches && item.branches.length > 0">

                                        <!-- Loop das duas primeiras empresas -->
                                        <div v-for="branch in item.branches.slice(0, 2)" :key="branch.id"
                                            :class="Number(branch.pivot.stock) <= Number(branch.pivot.min_stock)
                                                ? 'badge bg-danger-subtle border border-danger text-danger fw-bold d-flex align-items-center px-2 py-1 shadow-sm'
                                                : 'badge bg-white border border-light-subtle text-muted fw-medium d-flex align-items-center px-2 py-1 shadow-sm'"
                                            style="font-size: 0.75rem;">

                                            <i class="bi bi-shop me-1 opacity-75"
                                                :class="Number(branch.pivot.stock) <= Number(branch.pivot.min_stock) ? 'text-danger' : 'text-primary'"></i>
                                            <span class="text-truncate d-inline-block" style="max-width: 90px;"
                                                :title="branch.name">
                                                {{ branch.name }}
                                            </span>:
                                            <strong class="ms-1"
                                                :class="Number(branch.pivot.stock) <= Number(branch.pivot.min_stock) ? 'text-danger' : 'text-dark'">
                                                {{ branch.pivot.stock }}
                                            </strong>
                                            <!-- Mostra o estoque mínimo de forma discreta ao lado -->
                                            <span class="text-muted opacity-50 ms-0.5"
                                                style="font-size: 0.65rem;margin-left: 5px;">(mín:
                                                {{ parseInt(branch.pivot.min_stock) }})</span>
                                        </div>

                                        <!-- Dropdown para mais de 2 empresas -->
                                        <div class="dropdown" v-if="item.branches.length > 2">
                                            <div class="badge bg-light border border-light-subtle text-primary fw-bold d-flex align-items-center px-2 py-1 shadow-sm"
                                                style="font-size: 0.65rem; cursor: pointer;" data-bs-toggle="dropdown"
                                                data-bs-boundary="viewport">
                                                +{{ item.branches.length - 2 }} lojas <i
                                                    class="bi bi-chevron-down ms-1"></i>
                                            </div>

                                            <ul class="dropdown-menu shadow-sm border-0 p-2"
                                                style="min-width: 210px; font-size: 0.75rem;">
                                                <li v-for="branch in item.branches.slice(2)" :key="branch.id"
                                                    class="d-flex justify-content-between align-items-center py-1 px-2 border-bottom border-light">
                                                    <span class="text-muted text-truncate me-2"
                                                        style="max-width: 110px;" :title="branch.name">
                                                        <i class="bi bi-shop me-1 opacity-50"></i> {{ branch.name }}
                                                    </span>
                                                    <div class="d-flex align-items-center gap-1">
                                                        <strong
                                                            :class="Number(branch.pivot.stock) <= Number(branch.pivot.min_stock) ? 'text-danger' : 'text-dark'">
                                                            {{ branch.pivot.stock }}
                                                        </strong>
                                                        <span class="text-muted opacity-50" style="font-size: 0.6rem;">/
                                                            {{ parseInt(branch.pivot.min_stock) }}</span>
                                                    </div>
                                                </li>
                                            </ul>
                                        </div>

                                    </template>
                                    <span v-else class="text-muted opacity-50" style="font-size: 0.65rem;">
                                        Sem alocação
                                    </span>
                                </div>
                            </td>
                            <td class="text-end">
                                <div class="dropdown">
                                    <button class="btn btn-pmg-icon border" data-bs-toggle="dropdown"
                                        data-bs-popper-config='{"strategy":"fixed"}'>
                                        <i class="bi bi-three-dots-vertical"></i>
                                    </button>
                                    <ul class="dropdown-menu shadow border-0 py-2">
                                        <li><a class="dropdown-item fw-bold text-primary" href="#"
                                                @click.prevent="openStockModal(item)"><i
                                                    class="bi bi-box-seam me-2"></i>Movimentar Saldo</a></li>
                                        <li>
                                            <hr class="dropdown-divider">
                                        </li>
                                        <li><a class="dropdown-item fw-medium" href="#"
                                                @click.prevent="openHistoryModal(item)"><i
                                                    class="bi bi-list-check me-2"></i>Extrato do Serviço</a></li>

                                        <li><a class="dropdown-item" href="#"
                                                @click.prevent="openModal('edit', item)"><i
                                                    class="bi bi-pencil me-2"></i>Editar Serviço</a></li>
                                        <li>
                                            <hr class="dropdown-divider">
                                        </li>
                                        <li><a class="dropdown-item text-danger" href="#"
                                                @click.prevent="confirmDelete(item)"><i
                                                    class="bi bi-trash me-2"></i>Excluir</a></li>
                                    </ul>
                                </div>
                            </td>
                        </tr>
                    </tbody>

                    <tbody v-else>
                        <tr>
                            <td colspan="7" class="text-center py-5 text-muted">
                                <div class="d-flex flex-column align-items-center justify-content-center">
                                    <i class="bi bi-inbox fs-1 mb-2 text-secondary opacity-50"></i>
                                    <span class="fw-medium">Não há registros no momento.</span>
                                    <span class="small mt-1" v-if="filter.description">Tente limpar os filtros de
                                        busca.</span>
                                </div>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>

            <div class="text-center mt-4" v-if="hasMore">
                <button
                    class="btn btn-pmg-light px-5 btn-sm fw-bold d-inline-flex align-items-center justify-content-center"
                    @click="loadMore" :disabled="loading" style="min-width: 220px;">
                    <span v-if="loading" class="spinner-border spinner-border-sm me-2" role="status"
                        aria-hidden="true"></span>
                    <span>{{ loading ? 'Carregando dados...' : 'Carregar mais itens' }}</span>
                </button>
            </div>
        </div>

        <!-- Modal Novo/Editar Serviço -->
        <div class="modal fade" id="modalService" tabindex="-1" aria-hidden="true">
            <div class="modal-dialog modal-lg modal-dialog-centered">
                <div class="modal-content border-0 shadow-lg rounded-4">
                    <div class="modal-header border-0 pt-4 px-4">
                        <h5 class="fw-bold m-0">{{ isEditing ? 'Editar Serviço' : 'Novo Serviço' }}</h5>
                        <button type="button" class="btn-close" data-bs-dismiss="modal" id="closeModalBtn"></button>
                    </div>
                    <div class="modal-body px-4">
                        <div class="row g-3">
                            <div class="col-12">
                                <label class="form-label small fw-bold text-muted">Descrição <span
                                        class="text-danger">*</span></label>
                                <input v-model="form.description" type="text" class="form-control form-control-pmg"
                                    placeholder="Informe a descrição do serviço">
                            </div>

                            <div class="col-md-6">
                                <label class="form-label small fw-bold text-muted">Valor de Custo (R$)</label>
                                <input type="text" class="form-control form-control-pmg"
                                    placeholder="R$ 0,00 (Opcional)" :value="formatInputCurrency(form.cost_price)"
                                    @input="form.cost_price = parseInputCurrency($event)">
                            </div>

                            <div class="col-md-6">
                                <label class="form-label small fw-bold text-muted">Valor de Venda (R$) <span
                                        class="text-danger">*</span></label>
                                <input type="text" class="form-control form-control-pmg" placeholder="0,00"
                                    :value="formatInputCurrency(form.sale_price)"
                                    @input="form.sale_price = parseInputCurrency($event)">
                            </div>
                            <div class="col-md-6">
                                <label class="form-label small fw-bold text-muted">Estoque Mínimo</label>
                                <input v-model="form.min_stock" type="number" class="form-control form-control-pmg"
                                    placeholder="Ex: 5">
                            </div>
                            <div class="col-md-6">
                                <label class="form-label small fw-bold text-muted">Data/Hora de Expiração</label>
                                <input v-model="form.expires_at" type="datetime-local"
                                    class="form-control form-control-pmg">
                            </div>
                            <div class="col-12 mt-4 border-top pt-3">
                                <label class="form-label small fw-bold text-muted">Imagens do Serviço (Opcional)</label>

                                <div
                                    class="border d-flex flex-column align-items-center justify-content-center p-4 rounded-3 bg-light position-relative cursor-pointer border-dashed">
                                    <i class="bi bi-cloud-upload text-muted display-6 mb-2"></i>
                                    <span class="small fw-medium text-secondary">Clique para selecionar ou arraste as
                                        fotos</span>
                                    <span class="text-muted" style="font-size: 11px;">Formatos aceitos: JPEG, PNG, JPG,
                                        WEBP. Máx: 2MB.</span>

                                    <input type="file" @change="handleImageUpload" multiple accept="image/*"
                                        class="position-absolute w-100 h-100 top-0 start-0 opacity-0 cursor-pointer">
                                </div>

                                <div v-if="imagePreviews.length > 0" class="d-flex flex-wrap gap-2 mt-3">
                                    <div v-for="(img, idx) in imagePreviews" :key="idx"
                                        class="position-relative border rounded-3 overflow-hidden shadow-sm"
                                        style="width: 80px; height: 80px;">
                                        <img :src="img.url" class="w-100 h-100 object-fit-cover">

                                        <button type="button" @click="removeImage(idx)"
                                            class="btn btn-danger btn-sm position-absolute top-0 end-0 m-1 rounded-circle p-0 d-flex align-items-center justify-content-center"
                                            style="width: 20px; height: 20px; font-size: 12px;">
                                            <i class="bi bi-x"></i>
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div class="modal-footer border-0 pb-4 px-4 mt-2">
                        <button class="btn btn-pmg-light me-2" data-bs-dismiss="modal">Cancelar</button>
                        <button class="btn btn-pmg-primary px-4" :disabled="!isFormValid || loading"
                            @click="saveService">
                            <span v-if="loading" class="spinner-border spinner-border-sm me-2" role="status"></span>
                            {{ isEditing ? 'Atualizar' : 'Salvar Serviço' }}
                        </button>
                    </div>
                </div>
            </div>
        </div>

        <!-- Modal Confirmar Exclusão -->
        <div class="modal fade" id="confirmDeleteModal" tabindex="-1" aria-hidden="true">
            <div class="modal-dialog modal-sm modal-dialog-centered">
                <div class="modal-content border-0 shadow-lg rounded-4 text-center p-3">
                    <div class="modal-body">
                        <div class="text-danger mb-3">
                            <i class="bi bi-exclamation-triangle-fill fs-1"></i>
                        </div>
                        <h5 class="fw-bold">Tem certeza?</h5>
                        <p class="text-muted small">{{ isBulkDelete ? `Excluir ${selectedIds.length} serviços
                            selecionados?` : `Excluir "${itemToDelete?.description}"?` }}</p>
                    </div>
                    <div class="d-flex gap-2 justify-content-center pb-2">
                        <button class="btn btn-pmg-light px-4" data-bs-dismiss="modal">Cancelar</button>
                        <button class="btn btn-danger px-4 rounded-3" @click="executeDeletion">Confirmar</button>
                    </div>
                </div>
            </div>
        </div>
    </div>

    <!-- Modal de Movimentação de Saldo -->
    <div class="modal fade" id="modalStockMovement" tabindex="-1" aria-hidden="true" data-bs-backdrop="static">
        <div class="modal-dialog modal-dialog-centered">
            <div class="modal-content border-0 shadow-lg rounded-4">
                <div class="modal-header border-0 pt-3 px-4 pb-0">
                    <h5 class="fw-bold m-0 text-dark">Movimentar Saldo de Serviço</h5>
                    <button type="button" class="btn-close" data-bs-dismiss="modal"></button>
                </div>
                <div class="modal-body p-3 px-4">
                    <div class="d-flex align-items-center mb-3 p-2 bg-light rounded-3 border">
                        <div class="me-3">
                            <div class="bg-white p-2 rounded shadow-sm border"><i
                                    class="bi bi-cloud-check fs-4 text-primary"></i></div>
                        </div>
                        <div>
                            <h6 class="fw-bold mb-0 text-dark" style="font-size: 0.9rem;">{{
                                selectedServiceStock?.description }}</h6>
                        </div>
                    </div>

                    <div class="mb-3">
                        <label class="form-label ultra-small fw-bold text-muted text-uppercase mb-1">Saldos Atuais por
                            Empresa</label>
                        <div class="border rounded-3 overflow-hidden bg-white">
                            <div v-for="branch in selectedServiceStock?.branches" :key="branch.id"
                                class="d-flex justify-content-between align-items-center p-2 px-3 border-bottom last-child-no-border">
                                <span class="small text-dark"><i class="bi bi-building me-2 text-muted"></i>{{
                                    branch.name
                                }}</span>
                                <span class="text-primary fw-bold">{{ branch.pivot.stock }}</span>
                            </div>
                            <div v-if="!selectedServiceStock?.branches?.length"
                                class="small text-muted text-center py-2">
                                Sem saldo registrado.</div>
                        </div>
                    </div>

                    <div class="row g-2">
                        <div class="col-12">
                            <label class="form-label ultra-small fw-bold text-muted text-uppercase mb-1">Tipo de
                                Operação</label>
                            <select v-model="movementForm.type" class="form-select form-control-pmg py-2">
                                <option value="in">Entrada Manual (Crédito)</option>
                                <option value="out">Saída / Baixa Manual (Débito)</option>
                                <option value="transfer">Transferência entre empresas</option>
                            </select>
                        </div>

                        <!-- Exibe para Entrada ou Saída Padrão -->
                        <template v-if="movementForm.type !== 'transfer'">
                            <div class="col-md-7">
                                <label
                                    class="form-label ultra-small fw-bold text-muted text-uppercase mb-1">Empresa</label>
                                <select v-model="movementForm.branch_id" class="form-select form-control-pmg py-2">
                                    <option value="" disabled>Selecione...</option>
                                    <option v-for="b in branches" :key="b.id" :value="b.id">{{ b.name }}</option>
                                </select>
                            </div>
                            <div class="col-md-5">
                                <label
                                    class="form-label ultra-small fw-bold text-muted text-uppercase mb-1">Quantidade</label>
                                <input v-model="movementForm.quantity" type="number" step="0.01"
                                    class="form-control form-control-pmg py-2">
                            </div>
                        </template>

                        <template v-else>
                            <div class="col-md-6 mt-2">
                                <label class="form-label ultra-small fw-bold text-muted text-uppercase mb-1">Empresa
                                    Origem</label>
                                <select v-model="movementForm.branch_id" class="form-select form-control-pmg py-2">
                                    <option value="" disabled>Selecione a origem...</option>
                                    <option v-for="b in branches" :key="b.id" :value="b.id">{{ b.name }}</option>
                                </select>
                            </div>
                            <div class="col-md-6 mt-2">
                                <label class="form-label ultra-small fw-bold text-muted text-uppercase mb-1">Empresa
                                    Destino</label>
                                <select v-model="movementForm.destination_branch_id"
                                    class="form-select form-control-pmg py-2">
                                    <option value="" disabled>Selecione o destino...</option>
                                    <option v-for="b in branches" :key="b.id" :value="b.id">{{ b.name }}</option>
                                </select>
                            </div>
                            <div class="col-12 mt-2">
                                <label class="form-label ultra-small fw-bold text-muted text-uppercase mb-1">Quantidade
                                    a Transferir</label>
                                <input v-model="movementForm.quantity" type="number" step="0.01"
                                    class="form-control form-control-pmg py-2">
                            </div>
                        </template>

                        <div v-if="movementForm.type === 'in'" class="col-12 mt-2">
                            <div class="p-2 px-3 bg-light border rounded-4">
                                <span class="ultra-small fw-bold text-dark text-uppercase mb-2 d-block">Vincular a um
                                    Contrato/Lote (Opcional)</span>
                                <input v-model="movementForm.batch_number" type="text"
                                    class="form-control form-control-sm form-control-pmg py-2"
                                    placeholder="Ex: CONTRATO-001, VOUCHER-99">
                            </div>
                        </div>

                        <div class="col-12 mt-2">
                            <label class="form-label ultra-small fw-bold text-muted text-uppercase mb-1">Motivo /
                                Justificativa</label>
                            <textarea v-model="movementForm.reason" class="form-control form-control-pmg" rows="2"
                                placeholder="Ex: Ativação de contrato, ajuste..."></textarea>
                        </div>
                    </div>

                    <div class="mt-4">
                        <button class="btn btn-pmg-primary w-100 py-2 fw-bold" @click="executeMovement"
                            :disabled="!isMovementValid || loadingMovement">
                            <span v-if="loadingMovement" class="spinner-border spinner-border-sm me-2"></span>
                            <i v-else class="bi bi-check-circle me-2"></i> Confirmar Operação
                        </button>
                    </div>
                </div>
            </div>
        </div>
    </div>

    <!-- Modal Extrato de Serviços (ATUALIZADO COM FILTROS E NOVAS COLUNAS) -->
    <div class="modal fade" id="modalMovementHistory" tabindex="-1" aria-hidden="true">
        <div class="modal-dialog modal-xl modal-dialog-centered">
            <div class="modal-content border-0 shadow-lg rounded-4">
                <div class="modal-header border-0 pt-4 px-4 pb-2">
                    <div>
                        <h5 class="fw-bold m-0 text-dark">Extrato de Movimentações</h5>
                        <p class="text-muted small mb-0">Serviço: <strong>{{ selectedServiceHistory?.description
                                }}</strong>
                        </p>
                    </div>
                    <button type="button" class="btn-close" data-bs-dismiss="modal"></button>
                </div>
                <div class="modal-body p-4 pt-2">

                    <!-- Filtros Avançados (Adaptado para Bootstrap) -->
                    <div class="bg-light p-3 rounded-3 mb-4 border">
                        <div class="row g-3 align-items-end mb-3">
                            <div class="col-md-3">
                                <label class="form-label small fw-bold text-muted mb-1">Data Inicial (Mov.)</label>
                                <input type="date" v-model="historyFilters.start_date"
                                    class="form-control form-control-sm form-control-pmg">
                            </div>
                            <div class="col-md-3">
                                <label class="form-label small fw-bold text-muted mb-1">Data Final (Mov.)</label>
                                <input type="date" v-model="historyFilters.end_date"
                                    class="form-control form-control-sm form-control-pmg">
                            </div>
                            <div class="col-md-3">
                                <label class="form-label small fw-bold text-muted mb-1">Tipo de Operação</label>
                                <select v-model="historyFilters.type"
                                    class="form-select form-control-sm form-control-pmg">
                                    <option value="">Todos os Tipos</option>
                                    <option value="in">Entrada</option>
                                    <option value="out">Saída</option>
                                </select>
                            </div>
                            <div class="col-md-3">
                                <button type="button" @click="clearHistoryFilters"
                                    class="btn btn-pmg-light w-100 py-2 border shadow-sm">
                                    <i class="bi bi-arrow-counterclockwise me-1"></i> Limpar
                                </button>
                            </div>
                        </div>

                        <div class="row g-3 align-items-end">
                            <div class="col-md-3">
                                <label class="form-label small fw-bold text-muted mb-1">Vencimento (De)</label>
                                <input type="date" v-model="historyFilters.due_start_date"
                                    class="form-control form-control-sm form-control-pmg">
                            </div>
                            <div class="col-md-3">
                                <label class="form-label small fw-bold text-muted mb-1">Vencimento (Até)</label>
                                <input type="date" v-model="historyFilters.due_end_date"
                                    class="form-control form-control-sm form-control-pmg">
                            </div>
                            <div class="col-md-6">
                                <button type="button" @click="fetchHistoryMovements"
                                    class="btn btn-pmg-primary w-100 btn-sm py-2 fw-bold" :disabled="historyLoading">
                                    <span v-if="historyLoading" class="spinner-border spinner-border-sm me-2"></span>
                                    <i v-else class="bi bi-search me-1"></i> Aplicar Filtros Avançados
                                </button>
                            </div>
                        </div>
                    </div>

                    <!-- Cabeçalho Tabela (Exportação) -->
                    <div class="d-flex justify-content-between items-center mb-2">
                        <span class="small fw-bold text-muted text-uppercase"><i class="bi bi-list-ul me-1"></i>
                            Resultados
                            da busca</span>
                        <div class="d-flex gap-2">
                            <button class="btn btn-pmg-light btn-sm px-3 shadow-sm rounded-3 fw-bold"
                                @click="exportHistory('pdf')"><i
                                    class="bi bi-file-earmark-pdf-fill me-1 text-danger"></i>
                                PDF</button>
                            <button class="btn btn-pmg-light btn-sm px-3 shadow-sm rounded-3 fw-bold"
                                @click="exportHistory('excel')"><i
                                    class="bi bi-file-earmark-excel-fill me-1 text-success"></i> Excel</button>
                        </div>
                    </div>

                    <!-- Tabela de Histórico Completa -->
                    <div class="table-responsive border rounded-3" style="max-height: 400px;">
                        <table class="table table-hover align-middle mb-0">
                            <thead class="table-light sticky-top" style="z-index: 1;">
                                <tr>
                                    <th class="small fw-bold text-muted">DATA/HORA</th>
                                    <th class="small fw-bold text-muted">EMPRESA</th>
                                    <th class="small fw-bold text-muted">OPERAÇÃO</th>
                                    <th class="small fw-bold text-muted">QUANTIDADE</th>
                                    <th class="small fw-bold text-muted">LOTE / VALIDADE</th>
                                    <th class="small fw-bold text-muted">MOTIVO</th>
                                    <th class="small fw-bold text-muted">USUÁRIO</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-if="historyLoading">
                                    <td colspan="7" class="text-center py-4 text-muted">
                                        <span class="spinner-border spinner-border-sm me-2"></span> Carregando
                                        extrato...
                                    </td>
                                </tr>
                                <tr v-else-if="historyMovements.length === 0">
                                    <td colspan="7" class="text-center py-5 text-muted">
                                        <i class="bi bi-info-circle me-2"></i>Nenhuma movimentação encontrada para os
                                        filtros informados.
                                    </td>
                                </tr>
                                <tr v-for="log in historyMovements" :key="log.id">
                                    <td class="small text-muted whitespace-nowrap">{{ formatDate(log.created_at) }}</td>
                                    <td class="small fw-medium">{{ log.branch?.name || '-' }}</td>
                                    <td>
                                        <span class="badge rounded-pill px-2 py-1"
                                            :class="log.type === 'in' || log.type === 'ENTRADA' ? 'bg-success-subtle text-success border border-success-subtle' : 'bg-danger-subtle text-danger border border-danger-subtle'">
                                            <i
                                                :class="log.type === 'in' || log.type === 'ENTRADA' ? 'bi bi-arrow-down-left-circle me-1' : 'bi bi-arrow-up-right-circle me-1'"></i>
                                            {{ log.type === 'in' || log.type === 'ENTRADA' ? 'Entrada' : 'Saída' }}
                                        </span>
                                    </td>
                                    <td class="fw-bold">{{ Number(log.quantity).toFixed(2) }}</td>
                                    <td class="small text-muted">{{ log.batch?.code || log.batch_number || '-' }}</td>
                                    <td class="small text-truncate" style="max-width: 200px;" :title="log.reason">{{
                                        log.reason || '-' }}</td>
                                    <td class="small fw-medium text-dark"><i class="bi bi-person me-1 text-muted"></i>
                                        {{
                                            log.user?.name || 'Sistema' }}</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
                <div class="modal-footer border-0 pt-0 pb-4 justify-content-center">
                    <button class="btn btn-pmg-light w-100" data-bs-dismiss="modal">Fechar
                        Extrato</button>
                </div>
            </div>
        </div>
    </div>
    <div class="modal fade" id="modalServiceReport" tabindex="-1" aria-hidden="true">
        <div class="modal-dialog modal-lg modal-dialog-centered">
            <div class="modal-content border-0 shadow-lg rounded-4">
                <div class="modal-header border-0 pt-4 px-4 pb-2">
                    <h5 class="fw-bold m-0 text-dark">Filtros do Relatório de Serviços</h5>
                    <button type="button" class="btn-close" data-bs-dismiss="modal"></button>
                </div>

                <div class="modal-body p-4">
                    <div class="row g-3">
                        <!-- Período de Datas -->
                        <div class="col-md-6">
                            <label class="form-label small fw-bold text-muted">Data Inicial</label>
                            <input type="date" v-model="reportFilter.start_date" class="form-control form-control-pmg">
                        </div>
                        <div class="col-md-6">
                            <label class="form-label small fw-bold text-muted">Data Final</label>
                            <input type="date" v-model="reportFilter.end_date" class="form-control form-control-pmg">
                        </div>

                        <!-- Tipo de Operação -->
                        <div class="col-md-6">
                            <label class="form-label small fw-bold text-muted">Tipo de Movimentação</label>
                            <select v-model="reportFilter.type" class="form-select form-control-pmg">
                                <option value="ALL">Todas (Entradas, Saídas e Transferências)</option>
                                <option value="in">Apenas Entradas</option>
                                <option value="out">Apenas Saídas</option>
                                <option value="transfer">Apenas Transferências</option>
                            </select>
                        </div>

                        <!-- Busca textual / Descrição -->
                        <div class="col-md-6">
                            <label class="form-label small fw-bold text-muted">Filtrar por Descrição</label>
                            <input type="text" v-model="reportFilter.search" class="form-control form-control-pmg"
                                placeholder="Ex: Manutenção, Consultoria...">
                        </div>
                    </div>

                    <!-- Botão de Ação -->
                    <div class="mt-4 pt-2">
                        <button class="btn btn-pmg-primary w-100 py-3 fw-bold" :disabled="loadingReport"
                            @click="downloadServiceReport">
                            <template v-if="loadingReport">
                                <span class="spinner-border spinner-border-sm me-2"></span>
                                PROCESSANDO...
                            </template>
                            <template v-else>
                                <i :class="exportFormat === 'pdf' ? 'bi-file-earmark-pdf-fill' : 'bi-file-earmark-excel-fill'"
                                    class="me-2"></i>
                                {{ exportFormat === 'pdf' ? 'GERAR RELATÓRIO PDF' : 'EXPORTAR PLANILHA XLSX' }}
                            </template>
                        </button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import api from '@/services/api';

export default {
    inject: ['globalLoading'],
    data() {
        return {
            services: [],
            alerts: [],
            isEditing: false,
            form: {
                id: null,
                description: '',
                cost_price: '',
                sale_price: '',
                quantity: 0,
                min_stock: 0,
                expires_at: '',
                images: []
            },
            imagePreviews: [],
            selectedIds: [],
            filter: { description: '' },

            // Paginação
            totalItems: 0,
            currentPage: 1,
            lastPage: 1,
            loading: false,
            hasMore: false,

            showFilter: false,
            modalInstance: null,
            itemToDelete: null,
            isBulkDelete: false,
            branches: [],

            // Controle de Movimentação
            selectedServiceStock: null,
            loadingMovement: false,
            movementForm: {
                type: 'in',
                branch_id: '',
                destination_branch_id: '',
                quantity: '',
                reason: '',
                batch_number: '',
            },

            // Controle de Extrato / Histórico
            selectedServiceHistory: null,
            historyMovements: [],
            historyLoading: false,
            historyFilters: {
                start_date: '',
                end_date: '',
                type: '',
                due_start_date: '',
                due_end_date: ''
            },
            loadingReport: false,
            exportFormat: 'pdf',
            reportFilter: {
                start_date: '',
                end_date: '',
                type: 'ALL',
                search: ''
            }
        }
    },
    computed: {
        isAllSelected() {
            return this.services.length > 0 && this.services.every(i => this.selectedIds.includes(i.id));
        },
        isFormValid() {
            return this.form.description?.trim() &&
                this.form.sale_price !== '' &&
                this.form.sale_price !== null;
        },
        isMovementValid() {
            if (!this.movementForm.quantity || this.movementForm.quantity <= 0) return false;
            if (!this.movementForm.reason?.trim()) return false;
            if (this.movementForm.type === 'transfer') {
                return this.movementForm.branch_id && this.movementForm.destination_branch_id && (this.movementForm.branch_id !== this.movementForm.destination_branch_id);
            }
            return !!this.movementForm.branch_id;
        },
    },
    mounted() {
        const modalElement = document.getElementById('modalService');
        if (modalElement) {
            this.modalInstance = new window.bootstrap.Modal(modalElement);
        }
        this.fetchServices();
        this.fetchBranches();
    },
    methods: {
        formatCurrency(value) {
            if (value === null || value === undefined || value === '') return '-';
            return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);
        },
        formatDate(dateStr) {
            if (!dateStr) return '-';
            const date = new Date(dateStr);
            return date.toLocaleString('pt-BR', {
                day: '2-digit', month: '2-digit', year: 'numeric',
                hour: '2-digit', minute: '2-digit'
            });
        },
        handleFileUpload(event) {
            this.form.images = Array.from(event.target.files);
        },
        async fetchServices(page = 1, append = false) {
            this.globalLoading.show('Carregando serviços');
            this.loading = true;
            try {
                const params = {
                    page: page,
                    search: this.filter.description
                };
                const response = await api.get('/services', { params });
                const payload = response.data;
                const items = Array.isArray(payload) ? payload : (payload.data || []);
                const paginationData = payload.meta || payload;
                const { current_page, last_page, total } = paginationData;

                if (append) {
                    this.services = [...this.services, ...items];
                } else {
                    this.services = items;
                }

                this.currentPage = current_page;
                this.lastPage = last_page;
                this.totalItems = total;
                this.hasMore = this.currentPage < this.lastPage;

            } catch (error) {
                this.showAlert('Falha ao comunicar com o servidor.', 'error');
                console.error("Erro na API:", error);
            } finally {
                this.loading = false;
                this.globalLoading.hide();
            }
        },

        loadMore() {
            if (this.hasMore && !this.loading) {
                this.fetchServices(this.currentPage + 1, true);
            }
        },

        applyFilter() {
            this.showFilter = false;
            this.selectedIds = [];
            this.fetchServices(1, false);
        },

        showAlert(message, type = 'success') {
            const id = Date.now();
            let icon = 'bi-check-circle-fill';

            if (type === 'danger') icon = 'bi-exclamation-circle-fill';
            if (type === 'warning') icon = 'bi-exclamation-triangle-fill';
            if (type === 'error') icon = 'bi-x-circle-fill';

            this.alerts.push({ id, message, type, icon });

            const timeout = type === 'error' ? 6000 : 4000;
            setTimeout(() => this.removeAlert(id), timeout);
        },

        removeAlert(id) {
            this.alerts = this.alerts.filter(a => a.id !== id);
        },

        confirmDelete(target, isBulk = false) {
            this.itemToDelete = target;
            this.isBulkDelete = isBulk;
            const modal = new window.bootstrap.Modal(document.getElementById('confirmDeleteModal'));
            modal.show();
        },

        resetForm() {
        this.form = {
            id: null,
            description: '',
            cost_price: '',
            sale_price: '',
            min_stock: 0,
            quantity: 0,
            expires_at: '',
            images: []
        };
    },

        async saveService() {
            this.loading = true;

            try {
                const formData = new FormData();
                formData.append('description', this.form.description);
                formData.append('sale_price', this.form.sale_price);
                formData.append('min_stock', this.form.min_stock);
                if (this.form.cost_price) formData.append('cost_price', this.form.cost_price);
                formData.append('quantity', this.form.quantity || 0);

                if (this.form.expires_at) {
                    let formattedDate = this.form.expires_at.replace('T', ' ');
                    if (formattedDate.length === 16) formattedDate += ':00';
                    formData.append('expires_at', formattedDate);
                }

                this.imagePreviews.forEach((img) => {
                    if (img.isNew && img.file) {
                        formData.append('new_images[]', img.file);
                    } else if (!img.isNew && img.file_path) {
                        formData.append('existing_images[]', img.file_path);
                    }
                });

                if (this.isEditing) {
                    formData.append('_method', 'PUT');
                    await api.post(`/services/${this.form.id}`, formData, {
                        headers: { 'Content-Type': 'multipart/form-data' }
                    });

                    this.showAlert("Serviço atualizado com sucesso!");
                } else {
                    await api.post('/services', formData, {
                        headers: { 'Content-Type': 'multipart/form-data' }
                    });

                    this.showAlert("Serviço cadastrado com sucesso!");
                    this.resetForm();
                }

                this.fetchServices();
                if (this.modalInstance) this.modalInstance.hide();

            } catch (e) {
                if (e.response?.status === 403) {
                    this.showAlert("Permissão de acesso ao recurso não autorizada.", "error");
                }else if (e.response?.status === 422) {
                    const validationErrors = e.response.data?.data || e.response.data?.errors;
                    this.errors = validationErrors || {};
                    if (!validationErrors || Object.keys(validationErrors).length === 0) {
                        this.showAlert("Erro de validação. Verifique os campos preenchidos.", "error");
                        return;
                    }
                    let mensagensErro = "Os seguintes campos precisam de correção:\n\n";
                    for (const campo in validationErrors) {
                        mensagensErro += `- ${validationErrors[campo][0]}\n`;
                    }

                    this.showAlert(mensagensErro, "error");

                } else {
                    console.error("Erro desconhecido:", e);
                    this.showAlert("Erro ao salvar.", "error");
                }
            } finally {
                this.loading = false;
            }
        },

        async executeDeletion() {
            try {
                if (this.isBulkDelete) {
                    await api.post('/services/bulk-delete', {
                        ids: this.selectedIds
                    });
                    this.services = this.services.filter(s => !this.selectedIds.includes(s.id));
                    this.totalItems -= this.selectedIds.length;
                    this.showAlert(`${this.selectedIds.length} serviços excluídos.`);
                    this.selectedIds = [];
                } else {
                    await api.delete(`/services/${this.itemToDelete.id}`);
                    this.services = this.services.filter(s => s.id !== this.itemToDelete.id);
                    this.totalItems -= 1;
                    this.showAlert(`Serviço "${this.itemToDelete.description}" removido.`);
                }
            } catch (error) {
                console.error("Erro ao excluir registro(s):", error);
                const errorMessage = error.response?.data?.message || 'Erro ao comunicar com o servidor para excluir.';
                this.showAlert(errorMessage, 'error');
            } finally {
                const modalElement = document.getElementById('confirmDeleteModal');
                if (modalElement) {
                    const modalInstance = window.bootstrap.Modal.getInstance(modalElement);
                    if (modalInstance) {
                        modalInstance.hide();
                    }
                }
            }
        },

        toggleAll(e) {
            if (e.target.checked) this.selectedIds = this.services.map(s => s.id);
            else this.selectedIds = [];
        },

        openModal(mode, item = null) {
            if (mode === 'edit') {
                this.isEditing = true;
                let expiresStr = '';
                if (item.expires_at) {
                    expiresStr = item.expires_at.slice(0, 16);
                }

                this.form = {
                    ...item,
                    expires_at: expiresStr,
                    images: []
                };

                if (item.images && item.images.length > 0) {
                    this.imagePreviews = item.images.map(img => ({
                        url: img.url,
                        file_path: img.file_path,
                        isNew: false,
                        file: null
                    }));
                } else {
                    this.imagePreviews = [];
                }

            } else {
                this.isEditing = false;
                this.form = {
                    id: null, description: '', cost_price: '', sale_price: '', quantity: 0, expires_at: '', images: []
                };
                this.imagePreviews = [];
            }

            if (this.modalInstance) {
                this.modalInstance.show();
            }
        },

        formatInputCurrency(value) {
            if (value === null || value === '' || value === undefined) return '';
            const floatVal = parseFloat(value);
            if (isNaN(floatVal)) return '';

            return new Intl.NumberFormat('pt-BR', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
            }).format(floatVal);
        },

        parseInputCurrency(event) {
            let val = event.target.value.replace(/\D/g, '');
            if (!val) {
                event.target.value = '';
                return '';
            }

            const floatVal = (parseInt(val, 10) / 100).toFixed(2);
            event.target.value = new Intl.NumberFormat('pt-BR', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
            }).format(floatVal);

            return floatVal;
        },

        handleImageUpload(event) {
            const files = Array.from(event.target.files);

            files.forEach(file => {
                this.imagePreviews.push({
                    url: URL.createObjectURL(file),
                    isNew: true,
                    file: file
                });
            });

            event.target.value = '';
        },

        removeImage(index) {
            this.imagePreviews.splice(index, 1);
        },

        async fetchBranches() {
            try {
                const res = await api.get('/branches');
                this.branches = res.data.data || res.data;
            } catch (error) {
                console.error("Erro ao carregar empresas", error);
            }
        },

        openStockModal(service) {
            this.selectedServiceStock = service;
            this.movementForm = {
                type: 'in', branch_id: '', destination_branch_id: '', quantity: '', reason: '', batch_number: ''
            };
            window.bootstrap.Modal.getOrCreateInstance(document.getElementById('modalStockMovement')).show();
        },

        async executeMovement() {
            this.loadingMovement = true;
            try {
                await api.post(`/services/${this.selectedServiceStock.id}/move-stock`, this.movementForm);

                this.showAlert("Saldo atualizado com sucesso!");
                this.fetchServices(this.currentPage, false);
                window.bootstrap.Modal.getInstance(document.getElementById('modalStockMovement')).hide();
            } catch (error) {
                const msg = error.response?.data?.message || 'Falha ao realizar movimentação.';
                this.showAlert(msg, 'danger');
            } finally {
                this.loadingMovement = false;
            }
        },

        // --- MÉTODOS DO EXTRATO ---

        clearHistoryFilters() {
            this.historyFilters = {
                start_date: '', end_date: '', type: '', due_start_date: '', due_end_date: ''
            };
            this.fetchHistoryMovements();
        },

        async openHistoryModal(service) {
            this.selectedServiceHistory = service;
            this.historyMovements = [];
            // Limpa os filtros sempre que o modal for aberto para um novo serviço
            this.historyFilters = {
                start_date: '', end_date: '', type: '', due_start_date: '', due_end_date: ''
            };

            window.bootstrap.Modal.getOrCreateInstance(document.getElementById('modalMovementHistory')).show();

            // Dispara a busca inicial
            await this.fetchHistoryMovements();
        },

        async fetchHistoryMovements() {
            if (!this.selectedServiceHistory) return;

            this.historyLoading = true;
            try {
                const res = await api.get(`/services/${this.selectedServiceHistory.id}/movements`, {
                    params: this.historyFilters
                });
                this.historyMovements = res.data.data || res.data;
            } catch (e) {
                this.showAlert("Erro ao carregar o extrato do serviço.", "danger");
            } finally {
                this.historyLoading = false;
            }
        },

        async exportHistory(format) {
            // Mantém o estado de loading visual (se não tiver globalLoading, use historyLoading = true)
            this.globalLoading.show(`Gerando ${format.toUpperCase()}...`);

            try {
                const response = await api.get(`/services/${this.selectedServiceHistory.id}/movements/export`, {
                    params: { ...this.historyFilters, format },
                    responseType: 'blob' // Essencial para o Javascript entender o binário do arquivo
                });

                const extension = format === 'pdf' ? 'pdf' : 'xlsx';
                const url = window.URL.createObjectURL(new Blob([response.data]));
                const link = document.createElement('a');
                link.href = url;

                // Define o nome do arquivo
                link.setAttribute('download', `extrato_servico_${this.selectedServiceHistory.id}_${Date.now()}.${extension}`);

                document.body.appendChild(link);
                link.click();

                // Limpa o DOM removendo o link criado
                document.body.removeChild(link);
                window.URL.revokeObjectURL(url);

                this.showAlert(`Relatório ${format.toUpperCase()} gerado!`);
            } catch (e) {
                this.showAlert("Erro ao exportar arquivo.", "danger");
            } finally {
                this.globalLoading.hide();
            }
        },
        isGlobalStockCritical(item) {
            if (!item.branches || item.branches.length === 0) return false;
            // Retorna true se houver ao menos uma empresa com estoque real abaixo ou igual ao mínimo dela
            return item.branches.some(branch => Number(branch.pivot.stock) <= Number(branch.pivot.min_stock));
        },

        openReportModal(format = 'pdf') {
            // Normaliza 'xlsx' para 'excel' para bater com o v-model do modal
            this.exportFormat = (format === 'xlsx' || format === 'excel') ? 'excel' : 'pdf';

            // Abre o modal de Serviços via Instance do Bootstrap
            const modalEl = document.getElementById('modalServiceReport');
            if (modalEl) {
                const modal = new window.bootstrap.Modal(modalEl);
                modal.show();
            }
        },
        async downloadServiceReport() {
            const isPdf = this.exportFormat === 'pdf';

            this.globalLoading.show(isPdf ? 'Gerando relatório PDF...' : 'Gerando planilha Excel...');

            try {
                const response = await api.get('/services/export', {
                    params: { ...this.reportFilter, format: this.exportFormat },
                    responseType: 'blob'
                });

                // 1. Define o MimeType correto
                const mimeType = isPdf
                    ? 'application/pdf'
                    : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

                // 2. Define o nome do arquivo com a extensão correta (.pdf ou .xlsx)
                const fileName = isPdf
                    ? `relatorio_geral_servicos_${Date.now()}.pdf`
                    : `servicos_${Date.now()}.xlsx`;

                // 3. Cria o Blob com o MimeType explícito
                const blob = new Blob([response.data], { type: mimeType });
                const url = window.URL.createObjectURL(blob);

                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', fileName); // <-- Agora é dinâmico!
                document.body.appendChild(link);
                link.click();

                link.remove();
                window.URL.revokeObjectURL(url);

                // Fecha o modal
                const modalEl = document.getElementById('modalServiceReport');
                if (modalEl) {
                    const modalInstance = window.bootstrap.Modal.getInstance(modalEl);
                    if (modalInstance) modalInstance.hide();
                }

                // 4. Alerta dinâmico
                this.showAlert(isPdf ? "PDF gerado com sucesso!" : "Planilha gerada com sucesso!");

            } catch (e) {
                console.error(e);
                this.showAlert("Erro ao gerar relatório", "danger");
            } finally {
                this.globalLoading.hide();
            }
        },

        /**
         * Exporta a planilha de serviços em Excel (.xlsx)
         */
        async exportServicesExcel() {
            this.loading = true;
            this.globalLoading.show('Gerando Planilha...');
            try {
                const response = await api.get('/services/export', {
                    params: { ...this.reportFilter, format: 'excel' },
                    responseType: 'blob'
                });

                const url = window.URL.createObjectURL(new Blob([response.data]));
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', `servicos_${Date.now()}.xlsx`);
                document.body.appendChild(link);
                link.click();

                link.remove();
                window.URL.revokeObjectURL(url);

                const modalEl = document.getElementById('modalServiceReport');
                if (modalEl) {
                    const modalInstance = window.bootstrap.Modal.getInstance(modalEl);
                    if (modalInstance) modalInstance.hide();
                }

                this.showAlert("Planilha gerada com sucesso!");
            } catch (e) {
                console.error(e);
                this.showAlert("Erro ao exportar planilha.", "danger");
            } finally {
                this.loading = false;
                this.globalLoading.hide();
            }
        },
        refreshData() {
            this.fetchServices(1, false);
        },

    }
}
</script>

<style scoped>
/* ESTILOS DE ALERTA (TOASTS) MANTIDOS */
.toast {
    min-width: 300px;
    border-radius: 12px;
    background-color: white !important;
}

.bg-success-subtle {
    background-color: #ecfdf5 !important;
    color: #059669 !important;
    border-left: 5px solid #059669 !important;
}

.bg-danger-subtle {
    background-color: #fef2f2 !important;
    color: #dc2626 !important;
    border-left: 5px solid #dc2626 !important;
}

/* LAYOUT PMG MANTIDO */
.main-content {
    background-color: #f8fafc;
    min-height: 100vh;
    padding: 2rem;
    font-family: 'Inter', sans-serif;
}

.dropdown-menu {
    border-radius: 12px;
    box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1) !important;
    min-width: 200px;
}


.card {
    border-radius: 16px;
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.02);
}

.btn-pmg-primary {
    background: #561569;
    color: #fff;
    border-radius: 12px !important;
    padding: 10px 24px;
    font-weight: 600;
    transition: all 0.3s ease;
}

.btn-pmg-primary,
.btn-pmg-light,
.btn-pmg-danger {
    padding: 10px 24px !important;
    /* Aumentamos um pouco o preenchimento lateral */
    border-radius: 10px;
    font-weight: 500;
    font-size: 0.875rem;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    transition: all 0.2s ease;
    height: 45px;
    /* Fixamos a altura para garantir simetria total */
}

/* Cores específicas para o Primary */
.btn-pmg-primary {
    background: #561569;
    color: #fff;
    border: none;
}

.btn-pmg-primary:hover {
    background: #1a294d;
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(33, 51, 97, 0.2);
}

.btn-pmg-light {
    background: #fff;
    border: 1px solid #e2e8f0;
    border-radius: 10px;
    color: #64748b;
    padding: 10px 20px;
}

.btn-pmg-danger {
    background: #fee2e2;
    color: #b91c1c;
    border: 1px solid #fecaca;
    border-radius: 10px;
    font-weight: 600;
}

.form-control-pmg {
    border-radius: 10px;
    border: 1px solid #e2e8f0;
    padding: 12px;
}

.form-control-pmg:focus {
    border-color: #561569;
    box-shadow: 0 0 0 4px rgba(33, 51, 97, 0.1);
}

/* Tabela */
.table-pmg thead th {
    background: #f8fafc;
    color: #64748b;
    font-size: 0.7rem;
    text-transform: uppercase;
    letter-spacing: 1px;
    padding: 1.2rem;
    border-bottom: 1px solid #edf2f7;
}

.table-pmg tbody td {
    padding: 1.2rem;
    border-bottom: 1px solid #f1f5f9;
}

.pmg-desc-cell {
    color: #64748b;
    font-size: 0.9rem;
    max-width: 300px;
}

/* Transições */
.slide-fade-enter-active,
.slide-fade-leave-active {
    transition: all 0.4s ease;
}

.slide-fade-enter-from {
    transform: translateX(100px);
    opacity: 0;
}

.slide-fade-leave-to {
    opacity: 0;
    transform: scale(0.9);
}

.fade-enter-active,
.fade-leave-active {
    transition: opacity 0.3s;
}

.fade-enter-from,
.fade-leave-to {
    opacity: 0;
}

/* Estilos Adicionais Extrato */
.whitespace-nowrap {
    white-space: nowrap;
}

.bg-error-subtle {
    background-color: #fef2f2 !important;
    color: #991b1b !important;
    border-left: 5px solid #ef4444 !important;
}

.bg-warning-subtle {
    background-color: #fffbeb !important;
    color: #92400e !important;
    border-left: 5px solid #f59e0b !important;
}

.bg-error-subtle {
    animation: shake 0.4s ease-in-out;
}

@keyframes shake {

    0%,
    100% {
        transform: translateX(0);
    }

    25% {
        transform: translateX(-5px);
    }

    75% {
        transform: translateX(5px);
    }
}

.dropdown-item {
    font-size: 0.9rem;
    color: #334155;
    transition: all 0.2s ease;
}

.dropdown-item:hover {
    background-color: #f8fafc;
    color: #1e293b;
}

.dropdown-item.text-danger:hover {
    background-color: #fff1f2 !important;
    color: #dc2626 !important;
}

.btn-pmg-icon.border {
    border-color: #e2e8f0 !important;
    background: white;
    width: 38px;
    height: 38px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border-radius: 8px;
}

.btn-pmg-icon:hover {
    background-color: #f1f5f9;
}

.border-dashed {
    border: 2px dashed #e2e8f0 !important;
    transition: all 0.3s ease;
}

.border-dashed:hover {
    border-color: #561569 !important;
    background-color: #f1f5f9 !important;
}

.cursor-pointer {
    cursor: pointer !important;
}

.object-fit-cover {
    object-fit: cover;
}

.dropdown-item.text-danger:hover {
    background-color: #fff1f2 !important;
    color: #dc2626 !important;
}

.btn-pmg-light {
    border-radius: 12px !important;
    background-color: #fff;
    border: 1px solid #e2e8f0;
    color: #64748b;
    font-weight: 500;
}
</style>